/**
 * The interactive half of the panel kit: sliders, presets, toggles, and the
 * measure button. React (and, for the sliders, antd) editions of the
 * ui/shell.ts builders.
 *
 * The big simplification is that everything is CONTROLLED. knobBank's sync()
 * - the hand-written re-read every preset and assistant suggestion had to
 * remember to call - does not exist here: the sliders show whatever the
 * recipe says, because that is what controlled inputs are.
 */

import { Slider } from 'antd'
import { useState } from 'react'
import type { CSSProperties } from 'react'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../../app/i18n/LanguageProvider'

export interface KnobLimit {
  min: number
  max: number
  step: number
  unit: string
  label: string
}

function knobText(lim: KnobLimit, v: number, formatted?: string): string {
  if (formatted !== undefined) return formatted
  const decimals = lim.step < 1 ? (lim.step < 0.1 ? 2 : 1) : 0
  return `${v.toFixed(decimals)} ${lim.unit}`.trim()
}

/**
 * The slider bank. Catalogue label first, the limit's own English label as
 * the fallback - the same key-wins-else-source-of-record rule the fault text
 * and the preset notes follow.
 */
export function KnobBank<R>({
  recipe,
  limits,
  order,
  onChange,
  format,
  labelFor,
}: {
  recipe: R
  limits: Record<keyof R, KnobLimit>
  order: Array<keyof R>
  onChange: (patch: Partial<R>) => void
  format?: (key: keyof R, v: number) => string
  /**
   * Override the label lookup. Most tools share the `recipe.knob.*`
   * namespace; the two oldest panels (etch, deposition) name their knobs in
   * their own `tool.<id>.knob.*` catalogue and have to keep those keys.
   */
  labelFor?: (key: keyof R) => string
}) {
  useLang()
  return (
    <div>
      {order.map((key) => {
        const lim = limits[key]
        const value = recipe[key] as number
        return (
          <div className="knob" key={String(key)}>
            <div className="knob-row">
              <span className="name">
                {labelFor ? labelFor(key) : tOr(`recipe.knob.${String(key)}`, lim.label)}
              </span>
              <span className="val">{knobText(lim, value, format?.(key, value))}</span>
            </div>
            <Slider
              min={lim.min}
              max={lim.max}
              step={lim.step}
              value={value}
              tooltip={{ open: false }}
              onChange={(v: number) => onChange({ [key]: v } as Partial<R>)}
            />
          </div>
        )
      })}
    </div>
  )
}

/**
 * A stored-recipe list. The note under each recipe code comes from the
 * catalogue when there is an entry for it and from the preset's own English
 * note when there is not; the lookup is namespaced per tool so every
 * station's 'baseline' stays its own sentence.
 */
export function PresetList<R>({
  presets,
  onPick,
  ns,
}: {
  presets: Record<string, { label: string; note: string; recipe: R }>
  onPick: (recipe: R) => void
  ns?: string
}) {
  useLang()
  const prefix = ns ? `${ns}.` : ''
  return (
    <div>
      {Object.entries(presets).map(([key, p]) => (
        <button key={key} className="btn preset" onClick={() => onPick(p.recipe)}>
          <span className="plabel">{tOr(`recipe.presetLabel.${prefix}${key}`, p.label)}</span>
          <span className="pnote">{tOr(`recipe.preset.${prefix}${key}`, p.note)}</span>
        </button>
      ))}
    </div>
  )
}

/** A row of buttons where exactly one is active. Controlled, unlike the old one. */
export function ToggleRow({
  options,
  value,
  onChange,
  style,
}: {
  options: Array<{ value: string; label: string }>
  value: string
  onChange: (value: string) => void
  style?: CSSProperties
}) {
  return (
    <div className="btn-row" style={style}>
      {options.map((o) => (
        <button
          key={o.value}
          className={o.value === value ? 'btn primary toggle-opt' : 'btn toggle-opt'}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * A button that runs an expensive measurement, showing progress while it
 * does. The yielded beat before run() is what lets the busy label paint
 * before the main thread disappears into the solve.
 */
export function MeasureButton({
  label,
  run,
  primary = false,
}: {
  label: string
  run: () => Promise<void> | void
  primary?: boolean
}) {
  useLang()
  const [busy, setBusy] = useState(false)
  return (
    <button
      className={primary ? 'btn primary' : 'btn'}
      disabled={busy}
      onClick={() => {
        void (async () => {
          setBusy(true)
          await new Promise((r) => setTimeout(r, 0))
          try {
            await run()
          } finally {
            setBusy(false)
          }
        })()
      }}
    >
      {busy ? t('tool.common.Measuring') : label}
    </button>
  )
}
